/**
 * Navigation store using Zustand.
 * Tracks which main panel is active and controls nav visibility.
 */
import { create } from 'zustand';

export type MainPanel = 'chat' | 'camera' | 'faith';

interface NavigationState {
  // Current active panel (0=chat, 1=camera, 2=faith)
  activePanel: MainPanel;
  activePanelIndex: number;

  // Whether nav bar should be visible
  isNavVisible: boolean;

  // Whether user is in an immersive experience (camera recording, story viewing, etc.)
  isImmersive: boolean;

  /**
   * One-shot mailbox for ChatSection's chats/groups tab. A notification tap
   * can ask for the groups tab before the lazy-loaded ChatSection exists to
   * hear an event — the section consumes this on mount instead (FEAT-040).
   */
  pendingChatTab: 'chats' | 'groups' | null;
  requestChatTab: (tab: 'chats' | 'groups') => void;
  consumeChatTab: () => void;

  // Set active panel
  setActivePanel: (panel: MainPanel) => void;
  setActivePanelIndex: (index: number) => void;

  // Toggle nav visibility
  showNav: () => void;
  hideNav: () => void;

  // Immersive mode control
  enterImmersive: () => void;
  exitImmersive: () => void;
}

const panelToIndex: Record<MainPanel, number> = {
  chat: 0,
  camera: 1,
  faith: 2,
};

const indexToPanel: MainPanel[] = ['chat', 'camera', 'faith'];

export const useNavigationStore = create<NavigationState>((set) => ({
  // Default to chat (messages) panel - camera only when user swipes to it
  // This avoids iOS camera permission prompts on app open
  activePanel: 'chat',
  activePanelIndex: 0,

  isNavVisible: true,
  isImmersive: false,

  pendingChatTab: null,
  requestChatTab: (tab) => set({ pendingChatTab: tab }),
  consumeChatTab: () => set({ pendingChatTab: null }),

  setActivePanel: (panel: MainPanel) => {
    set({
      activePanel: panel,
      activePanelIndex: panelToIndex[panel],
    });
  },

  setActivePanelIndex: (index: number) => {
    const clampedIndex = Math.max(0, Math.min(2, index));
    set({
      activePanel: indexToPanel[clampedIndex],
      activePanelIndex: clampedIndex,
    });
  },

  showNav: () => set({ isNavVisible: true }),
  hideNav: () => set({ isNavVisible: false }),

  enterImmersive: () => set({ isImmersive: true, isNavVisible: false }),
  exitImmersive: () => set({ isImmersive: false, isNavVisible: true }),
}));
